import moment from 'moment';
import { sanitizeLodgingProvider } from 'utils/Helpers';

function searchDuration(createdAt) {
  const mCreatedAt = moment(createdAt);
  const mNow = moment();
  const duration = moment.duration(mNow.diff(mCreatedAt));
  return duration.asSeconds();
}

/**
 * Calculates closed trips metrics from a list of closed trips.
 * @param {Object} closedTrips - Immutable List of closed trips.
 * @returns {Object} - Metrics for closed trips: pastTripsCount, soldOutTripsCount, closedTripsCount, nonAvailableTripsTypesShown.
 */
function getClosedTripsMetrics(closedTrips) {
  if (!closedTrips || closedTrips.size === 0) {
    return {
      pastTripsCount: 0,
      soldOutTripsCount: 0,
      closedTripsCount: 0,
      nonAvailableTripsTypesShown: [],
    };
  }

  const now = moment();
  let pastTripsCount = 0;
  let soldOutTripsCount = 0;
  const typesShown = new Set();

  closedTrips.forEach((trip) => {
    const availability = trip.get('availableSeats') || trip.get('available_seats') || 0;
    const departure = trip.get('departure');
    const isDeparted = moment(departure).isBefore(now);

    if (availability === 0) {
      soldOutTripsCount += 1;
      typesShown.add('sold_out');
    } else if (isDeparted) {
      pastTripsCount += 1;
      typesShown.add('past');
    } else {
      // Closed for other reasons (e.g., closed by operator)
      typesShown.add('closed');
    }
  });

  return {
    pastTripsCount,
    soldOutTripsCount,
    closedTripsCount: closedTrips.size,
    nonAvailableTripsTypesShown: Array.from(typesShown),
  };
}

export default function getSearchAnalyticsData({
  search,
  places,
  departures,
  returns,
  lodgingProvider,
  openTicket = false,
}) {
  const origin = places[search.get('originId')];
  const destination = places[search.get('destinationId')];
  const returnDate = search.get('returnDate');

  const analyticsData = {
    search: {
      origin,
      destination,
      id: search.getIn(['departure', 'id']),
      roundTrip: !!returnDate,
      departureDate: openTicket ? null : moment(search.get('departureDate'), 'DD-MM-YYYY').toDate(),
      returnDate: !openTicket && returnDate && moment(returnDate, 'DD-MM-YYYY').toDate(),
      passengers: search.get('passengers').toJS(),
      openTicket,
    },
    departureData: {
      busCount: 0,
      flightCount: 0,
      multicarrierCount: 0,
      totalResultsShown: 0,
      pastTripsCount: 0,
      soldOutTripsCount: 0,
      closedTripsCount: 0,
      afterMidnightCount: 0,
      nonAvailableTripsTypesShown: [],
    },
  };
  if (lodgingProvider) {
    analyticsData.search.lodgingProvider = sanitizeLodgingProvider(lodgingProvider);
  }
  if (departures) {
    const typeOfTransport = search.getIn(['departure', 'typeOfTransport']);
    const buses = departures.get('buses');

    if (buses) {
      const trips = buses.get('trips');
      const closedTrips = buses.get('closedTrips');
      const tripsCount = trips ? trips.size : 0;
      const closedTripsMetrics = getClosedTripsMetrics(closedTrips);

      const afterMidnightTrips = buses.getIn(['afterMidnight', 'trips']);

      analyticsData.departureData.busCount = tripsCount;
      // The after-midnight section is counted on its own and deliberately NOT added here. The issue
      // puts it outside the result counter, and folding it in would move `Total Results Shown` in
      // the very cycle the feature is being measured, breaking comparability with the baseline.
      analyticsData.departureData.totalResultsShown =
        tripsCount + closedTripsMetrics.closedTripsCount;
      analyticsData.departureData.afterMidnightCount = afterMidnightTrips
        ? afterMidnightTrips.size
        : 0;
      analyticsData.departureData.pastTripsCount = closedTripsMetrics.pastTripsCount;
      analyticsData.departureData.soldOutTripsCount = closedTripsMetrics.soldOutTripsCount;
      analyticsData.departureData.closedTripsCount = closedTripsMetrics.closedTripsCount;
      analyticsData.departureData.nonAvailableTripsTypesShown =
        closedTripsMetrics.nonAvailableTripsTypesShown;
    }

    analyticsData.departureData.busActive = typeOfTransport.bus.active;

    analyticsData.departureData.parentSearchId = search.getIn(['departure', 'parentSearchId']);
    analyticsData.departureData.searchDuration = searchDuration(
      search.getIn(['departure', 'createdAt']),
    );
  }

  if (returns) {
    const typeOfTransport = search.getIn(['return', 'typeOfTransport']);
    const buses = returns.get('buses');

    analyticsData.returnData = {
      busCount: 0,
      flightCount: 0,
      totalResultsShown: 0,
      pastTripsCount: 0,
      soldOutTripsCount: 0,
      closedTripsCount: 0,
      nonAvailableTripsTypesShown: [],
    };

    if (buses) {
      const trips = buses.get('trips');
      const closedTrips = buses.get('closedTrips');
      const tripsCount = trips ? trips.size : 0;
      const closedTripsMetrics = getClosedTripsMetrics(closedTrips);

      analyticsData.returnData.busCount = tripsCount;
      analyticsData.returnData.totalResultsShown = tripsCount + closedTripsMetrics.closedTripsCount;
      analyticsData.returnData.pastTripsCount = closedTripsMetrics.pastTripsCount;
      analyticsData.returnData.soldOutTripsCount = closedTripsMetrics.soldOutTripsCount;
      analyticsData.returnData.closedTripsCount = closedTripsMetrics.closedTripsCount;
      analyticsData.returnData.nonAvailableTripsTypesShown =
        closedTripsMetrics.nonAvailableTripsTypesShown;
    }

    analyticsData.returnData.busActive = typeOfTransport.bus.active;

    analyticsData.returnData.parentSearchId = search.getIn(['return', 'parentSearchId']);
    analyticsData.returnData.searchDuration = searchDuration(search.getIn(['return', 'createdAt']));
  }

  return analyticsData;
}
